import React from "react";
import { Button, Typography } from "../common";
import CongratulationIcon from "../../images/CongratulationIcon";
import { useNavigate } from "react-router-dom";
import { useLazyGetLevelByIdQuery } from "../../store/api/masterCourseApi";
import {
  findNextIncompleteLessonInLevel,
  findRemainingLevel,
  firstIncompleteUnlockedLesson,
  levelsIncludedInPlan,
} from "../../pages/LessonPlayer/lessonCompletionUtils";

interface LessonInfo {
  id: string;
  order?: number;
  status: string;
}

interface CongratulationsViewProps {
  // These props are kept in the interface for backwards compatibility
  isFinalLesson?: boolean;
  onViewCertificate?: () => void;
  // Props for next lesson navigation
  courseId?: string;
  currentLevelId?: string;
  levelTitle?: string;
  levelNumber?: number;
  masterCourseId?: string;
  masterCourseTitle?: string;
  allLevels?: Array<{
    id: string;
    levelNumber: number;
    title: string;
    status: string;
  }>;
  lessons?: LessonInfo[];
  currentLessonId?: string;
  variant?: "completed" | "skipped";
  showCertificateAction?: boolean;
  onContinue?: () => void;
  continueLoading?: boolean;
}

const CongratulationsView: React.FC<CongratulationsViewProps> = ({
  onContinue,
  continueLoading = false,
  courseId,
  currentLevelId,
  levelTitle,
  levelNumber,
  masterCourseId,
  masterCourseTitle,
  allLevels,
  lessons,
  currentLessonId,
  variant = "completed",
  showCertificateAction = false,
}) => {
  const navigate = useNavigate();
  const [getLevelById] = useLazyGetLevelByIdQuery();

  const buildLessonState = (
    targetLessons: LessonInfo[],
    levelContext?: {
      id?: string;
      title?: string;
      number?: number;
    }
  ) => ({
    courseId: levelContext?.id || currentLevelId || courseId,
    lessons: [...targetLessons].sort((a, b) => (a.order || 0) - (b.order || 0)).map((lesson) => ({
      id: lesson.id,
      order: lesson.order,
      status: lesson.status,
    })),
    levelId: levelContext?.id || currentLevelId,
    levelTitle: levelContext?.title || levelTitle,
    levelNumber: levelContext?.number || levelNumber,
    masterCourseId,
    masterCourseTitle,
    allLevels,
    showCertificateAction,
  });

  const isLastLessonInLevel = (targetLessons: LessonInfo[] | undefined) => {
    if (!targetLessons || !currentLessonId || targetLessons.length === 0) {
      return false;
    }

    const sortedLessons = [...targetLessons].sort((a, b) => (a.order || 0) - (b.order || 0));
    const currentIndex = sortedLessons.findIndex((lesson) => lesson.id === currentLessonId);
    return currentIndex >= 0 && currentIndex === sortedLessons.length - 1;
  };

  const handleContinue = async () => {
    if (continueLoading) {return;}
    if (onContinue) { onContinue(); return; }
    let currentLevelLessons = lessons;
    if (currentLevelId) {
      try {
        const currentLevelDetail = await getLevelById(currentLevelId).unwrap();
        if (currentLevelDetail?.lessons?.length) {
          currentLevelLessons = currentLevelDetail.lessons;
        }
      } catch (error) {
        console.error("Failed to refresh current level before next lesson navigation:", error);
      }
    }

    const nextLesson = findNextIncompleteLessonInLevel(currentLevelLessons, currentLessonId);
    if (nextLesson && currentLevelLessons) {
      navigate(`/lesson/${nextLesson.id}`, {
        state: buildLessonState(currentLevelLessons),
        replace: true
      });
      return;
    }

    if (!isLastLessonInLevel(currentLevelLessons)) {
      if (masterCourseId && currentLevelId) {
        navigate(`/master-courses/${masterCourseId}/levels/${currentLevelId}`, {
          replace: true,
        });
        return;
      }
      if (masterCourseId) {
        navigate(`/master-courses/${masterCourseId}`, { replace: true });
        return;
      }
    }

    const nextLevel = findRemainingLevel(levelsIncludedInPlan(allLevels), levelNumber);
    if (nextLevel && masterCourseId) {
      try {
        const nextLevelDetail = await getLevelById(nextLevel.id).unwrap();
        const nextLevelLessons = nextLevelDetail?.lessons || [];
        const firstLesson = firstIncompleteUnlockedLesson(nextLevelLessons);

        if (firstLesson && nextLevelLessons.length > 0) {
          navigate(`/lesson/${firstLesson.id}`, {
            state: buildLessonState(nextLevelLessons, {
              id: nextLevel.id,
              title: nextLevel.title,
              number: nextLevel.levelNumber,
            }),
            replace: true,
          });
          return;
        }

        navigate(`/master-courses/${masterCourseId}/levels/${nextLevel.id}`, {
          replace: true,
        });
        return;
      } catch (error) {
        console.error("Failed to resolve next level after lesson completion:", error);
        navigate(`/master-courses/${masterCourseId}`, { replace: true });
        return;
      }
    }

    if (masterCourseId) {
      navigate(`/master-courses/${masterCourseId}`, { replace: true });
      return;
    }

    navigate("/courses", { replace: true });
  };

  return (
    <div
      data-testid="lesson-completion-congratulations"
      className="w-full h-full flex-grow flex flex-col items-center justify-center p-4 bg-white rounded-[10px]"
    >
      <div className="flex flex-col items-center justify-center rounded-lg p-6 max-w-lg w-full">
        {/* Congratulation Image */}
        <div className="mb-6 w-full flex justify-center">
          <CongratulationIcon className="w-full max-w-xs" />
        </div>

        {/* Heading */}
        <Typography
          variant="h4"
          weight="bold"
          data-testid="lesson-completion-heading"
        >
          {variant === "skipped" ? "Unit skipped" : "Unit Completed"}
        </Typography>

        {/* Subtitle */}
        <Typography
          variant="p"
          color="none"
          className="text-grey-600 mb-8 text-center"
        >
          {variant === "skipped"
            ? "This optional unit now counts towards your progress. You can complete it later from the module page."
            : "You have completed this unit."}
        </Typography>

        {/* Continue Button */}
        {continueLoading && <p role="status" className="text-grey-600 mb-4 text-center">Loading next unit…</p>}
        <div className="w-1/2">
          <Button
            variant="filled"
            size="large"
            onClick={handleContinue}
            disabled={continueLoading}
            aria-busy={continueLoading}
            className="w-full text-p font-medium transition-all duration-300"
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CongratulationsView;
